'use client';
import type {ReactNode} from 'react';
import {Box} from 'lucide-react';
import {cn} from '@/lib/utils';

// Fixed "MM/DD HH:mm" (local time) instead of toLocaleString: Node's ICU and the
// browser format 'mn-MN' differently, which breaks hydration.
const pad = (n: number) => String(n).padStart(2, '0');
export const when = (v: string | number | null | undefined) => {
  if (!v) return '—';
  const d = new Date(typeof v === 'number' ? v * 1000 : v);
  return `${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
export const duration = (v: number | null | undefined) => v === null || v === undefined ? '—' : `${(v / 1000).toFixed(1)} сек`;

export const statusLabels: Record<string, string> = {
  completed: 'Амжилттай', failed: 'Алдаатай', running: 'Ажиллаж байна', queued: 'Хүлээж байна', cancelled: 'Цуцалсан',
  stopped: 'Зогссон', starting: 'Эхэлж байна', ready: 'Бэлэн', crashed: 'Унасан', active: 'Идэвхтэй', disabled: 'Идэвхгүй',
};
const tone = (s: string) => ['completed', 'ready', 'active'].includes(s) ? 'success' : ['failed', 'crashed'].includes(s) ? 'danger' : ['running', 'queued', 'starting'].includes(s) ? 'progress' : 'neutral';
const toneClass: Record<string, string> = {
  success: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 [&_i]:bg-emerald-500',
  danger: 'bg-pink-500/10 text-pink-700 dark:text-pink-300 [&_i]:bg-pink-500',
  progress: 'bg-violet-500/10 text-violet-700 dark:text-violet-300 [&_i]:bg-violet-500',
  neutral: 'bg-muted text-muted-foreground [&_i]:bg-muted-foreground',
};

export function StatusBadge({status, className}: {status: string; className?: string}) {
  const live = ['running', 'starting', 'ready'].includes(status);
  return <span className={cn('inline-flex h-6 shrink-0 items-center gap-1.5 rounded-full px-2.5 text-xs font-medium whitespace-nowrap', toneClass[tone(status)], className)}>
    <i className={cn('size-1.5 rounded-full', live && 'pulse')} />{statusLabels[status] || status}
  </span>;
}

export function PageHeader({title, description, children}: {title: string; description?: string; children?: ReactNode}) {
  return <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
    <div><h1 className="text-2xl font-semibold tracking-tight">{title}</h1>{description && <p className="mt-1 text-sm text-muted-foreground">{description}</p>}</div>
    {children && <div className="flex flex-wrap items-center gap-2">{children}</div>}
  </div>;
}

export function Empty({title, children, icon}: {title: string; children?: ReactNode; icon?: ReactNode}) {
  return <div className="flex flex-col items-center gap-3 px-4 py-14 text-center">
    <span className="flex size-12 items-center justify-center rounded-2xl gradient-soft text-brand-violet">{icon ?? <Box className="size-5" />}</span>
    <strong className="text-sm">{title}</strong>
    {children && <p className="max-w-sm text-sm text-muted-foreground">{children}</p>}
  </div>;
}

export function Field({label, hint, children, className}: {label: ReactNode; hint?: ReactNode; children: ReactNode; className?: string}) {
  return <label className={cn('flex flex-col gap-2 text-sm font-medium', className)}>
    <span className="flex items-baseline justify-between gap-3">{label}{hint && <small className="text-xs font-normal text-muted-foreground">{hint}</small>}</span>
    {children}
  </label>;
}

export function Notice({tone = 'error', children}: {tone?: 'error' | 'ok'; children: ReactNode}) {
  return <div role={tone === 'error' ? 'alert' : 'status'} className={cn('rounded-xl px-4 py-3 text-sm', tone === 'error' ? 'bg-pink-500/10 text-pink-700 dark:text-pink-300' : 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300')}>{children}</div>;
}
